import { isUnknownArray } from "../types/data";
// Keep principal and supporting cast while excluding the very long aggregate
// guest-cast tail returned for long-running series.
import type { PersonCredit } from "../types/media";
export const COMPACT_CAST_LIMIT = 30;
type CompactPerson = PersonCredit & { name: string };

const compactJobs = (person: PersonCredit | undefined | null) => {
  const jobs = [
    ...(person?.job ? [{
      job: person.job,
      ...(person?.department ? { department: person.department } : {})
    }] : []),
    ...(isUnknownArray(person?.jobs) ? person?.jobs || [] : [])
  ]
    .map((item) => ({
      ...(item?.job ? { job: item.job } : {}),
      ...(item?.department ? { department: item.department } : {}),
      ...(Number.isFinite(Number(item?.episode_count))
        ? { episode_count: Number(item.episode_count) }
        : {})
    }))
    .filter((item) => item.job || item.department);

  return Array.from(new Map(jobs.map((item) => [
    `${String(item.job || "").toLowerCase()}|${String(item.department || "").toLowerCase()}`,
    item
  ])).values()).slice(0, 8);
};

export const getCrewJobNames = (member: PersonCredit | undefined | null): string[] => [
  member?.job,
  ...compactJobs(member).map((item) => item.job)
].filter((job): job is string => Boolean(job));

const normalizeCrewValue = (value: unknown) => String(value || "").trim().toLowerCase();

export const isDirectorCrewMember = (member: PersonCredit | undefined | null) => {
  const department = normalizeCrewValue(
    member?.department || member?.known_for_department
  );
  const jobs = getCrewJobNames(member).map(normalizeCrewValue);
  const hasDirectorJob = jobs.some((job) =>
    job === "director" ||
    job === "series director" ||
    job === "co-director" ||
    job === "режиссёр" ||
    job === "режиссер"
  );

  return hasDirectorJob || (
    jobs.length === 0 &&
    (department === "directing" || department === "режиссура")
  );
};

export const isComposerCrewMember = (member: PersonCredit | undefined | null) => {
  const department = normalizeCrewValue(
    member?.department || member?.known_for_department
  );
  const jobs = getCrewJobNames(member).map(normalizeCrewValue);
  const hasComposerJob = jobs.some((job) =>
    job.includes("composer") ||
    job.includes("композитор") ||
    job === "music" ||
    job === "music by" ||
    job.includes("original score") ||
    job.includes("theme music")
  );

  return hasComposerJob || (jobs.length === 0 && department === "music");
};

const compactPerson = (person: PersonCredit | undefined | null): CompactPerson | null => {
  if (!person?.id && !person?.name && !person?.original_name) return null;
  const roles = isUnknownArray(person.roles)
    ? person.roles
      .map((role) => ({
        ...(role?.character ? { character: role.character } : {}),
        ...(role?.role ? { role: role.role } : {})
      }))
      .filter((role) => role.character || role.role)
      .slice(0, 6)
    : [];
  const jobs = compactJobs(person);
  const primaryJob =
    person.job ||
    jobs.find((item) => String(item.job || "").toLowerCase().includes("composer"))?.job ||
    jobs[0]?.job;

  return {
    ...(person.id != null ? { id: person.id } : {}),
    name: person.name || person.original_name || "Неизвестно",
    ...(person.original_name ? { original_name: person.original_name } : {}),
    ...(person.profile_path ? { profile_path: person.profile_path } : {}),
    ...(person.department ? { department: person.department } : {}),
    ...(person.known_for_department
      ? { known_for_department: person.known_for_department }
      : {}),
    ...(primaryJob ? { job: primaryJob } : {}),
    ...(jobs.length ? { jobs } : {}),
    ...(person.character ? { character: person.character } : {}),
    ...(person.role ? { role: person.role } : {}),
    ...(roles.length ? { roles } : {})
  };
};

const isStatisticsCrewMember = (member: PersonCredit) => {
  const department = member?.department || member?.known_for_department;
  const jobs = getCrewJobNames(member).map((job) => String(job).toLowerCase());

  return (
    ["Directing", "Writing", "Art", "Editing"].includes(department || "") ||
    isDirectorCrewMember(member) ||
    isComposerCrewMember(member) ||
    jobs.some((job) =>
      job.includes("writer") ||
      job.includes("screenplay") ||
      job === "editor" || job.endsWith(" editor")
    )
  );
};

const getCrewRoles = (member: PersonCredit) => {
  const department = member?.department || member?.known_for_department;
  const jobs = getCrewJobNames(member).map((job) => String(job).toLowerCase());
  const roles: ("directors" | "writers" | "composers" | "artists" | "editors")[] = [];
  if (isDirectorCrewMember(member)) roles.push("directors");
  if (department === "Writing" || jobs.some((job) => /writer|screenplay|story/.test(job))) roles.push("writers");
  if (isComposerCrewMember(member)) roles.push("composers");
  if (department === "Art") roles.push("artists");
  if (department === "Editing" || jobs.some((job) => job === "editor" || job.endsWith(" editor"))) roles.push("editors");
  return roles;
};

const mergeCompactPeople = (existing: CompactPerson | undefined, next: CompactPerson): CompactPerson => {
  if (!existing) return next;
  const jobs = compactJobs({
    jobs: [
      ...(existing.jobs || []),
      ...(next.jobs || [])
    ]
  });

  return {
    ...next,
    ...existing,
    ...(existing.job || next.job ? { job: existing.job || next.job } : {}),
    ...(jobs.length ? { jobs } : {})
  };
};

const compactCrew = (crew: PersonCredit[]): CompactPerson[] => {
  const merged = new Map<string, CompactPerson>();
  const counts = new Map<string, number>();

  crew.forEach((member) => {
    const compact = compactPerson(member);
    if (!compact) return;
    const key = compact.id != null ? String(compact.id) : compact.name.toLowerCase();
    merged.set(key, mergeCompactPeople(merged.get(key), compact));
  });

  const selected: CompactPerson[] = [];
  merged.forEach((member) => {
    const roles = getCrewRoles(member);
    const availableRoles = roles.filter((role) => (counts.get(role) || 0) < 10);
    if (availableRoles.length === 0) return;

    availableRoles.forEach((role) => counts.set(role, (counts.get(role) || 0) + 1));
    selected.push(member);
  });

  return selected;
};

export const compactFilmCredits = (credits: { cast?: PersonCredit[]; crew?: PersonCredit[] } = {}) => ({
  cast: (isUnknownArray(credits.cast) ? credits.cast : [])
    .slice(0, COMPACT_CAST_LIMIT)
    .map(compactPerson)
    .filter((person): person is CompactPerson => person !== null),
  crew: compactCrew(
    (isUnknownArray(credits.crew) ? credits.crew : []).filter(isStatisticsCrewMember)
  )
});
